import {addAnnotationsActionHandlers } from "./account/annotations";
import {displayNotice} from "./notices/functions";
import FetchHelpers from "./fetch-helpers";
import {Spinner} from "./spinner";
import {AsyncNotice} from "./notices/notice";
import {addPinEventHandlers} from "./account/last-locations";

/**
 * The paginator for tables has an Items Per Page dropdown selector that can be used
 * to let the user change the number of items displayed on the table.
 * The following event handler is added if the select.items_per_page element is found on the page
 * The dropdown can be found in /src/Models/Pagination::renderItemsPerPage()
 */

/**
 * Event handler for the "Items per Page" select on top of a table
 * @param event
 * @param options
 * @returns {Promise<void>}
 */
async function itemsPerPageHandler(event, options) {
  event.preventDefault();
  try {
    const refreshUrl = event.target.dataset.refreshUrl;
    // const paginatorName = event.target.dataset.paginatorName;
    // const paginationControlsSelector = `nav#${paginatorName}-nav`;
    const {collection: {paginatorName, paginationControlsSelector}} = options;
    let sortByElem = document.querySelector('#sort-by');
    let sort = 'date-desc';

    if (sortByElem) {
      sort = sortByElem.value;
      options.collection.sort = sort;
    }

    options.collection.paginatorName = paginatorName;
    options.collection.paginationControlsSelector = paginationControlsSelector;
    options.collection.itemsPerPage = parseInt(event.target.value);
    options.spinner.name = `${paginatorName}-paginator`;

    // passing 'currentPage' as '1' so if you change the page size, you are back at the first page
    // TODO: we could do the math to calculate how to adjust current page on an items-per-page change...
    const data = {
      options,
      currentPage: '1'
    };

    const result = await handleCollectionUpdate(refreshUrl, data);
  } catch (error) {
    console.log(`Error: ${error.message} on ${error.lineNumber}`);
    const msg = 'There was an error and the collection could not be updated.'
    const notice = new AsyncNotice(msg, 'error');
    await notice.create({}, true);
    notice.remove();
  }
}

/**
 * Handles pagination event and updates the collection based on the selected page number.
 *
 * @param {Event} event - The event object representing the pagination event.
 * @returns {Promise<void>} - A Promise that resolves once the collection is updated.
 */
async function paginationHandler(event) {
  event.preventDefault();

  const navElem = event.target.closest('nav');
  if (navElem) {
    try {
      const paginatorName = navElem.dataset.paginatorName;
      const paginationControlsSelector = `nav#${paginatorName}-nav`;
      const itemsPerPageElem = document.querySelector(`#${paginatorName}-items-per-page`);
      const collectionElem = document.querySelector(`#${paginatorName}-collection`);
      const refreshUrl = collectionElem.dataset.refreshUrl;
      const options = JSON.parse(collectionElem.dataset.options);
      const { dataset: { pageNumber } } = event.target.closest('a'); // ensure the event target is the pagination 'a' link

      options.collection.currentPage = pageNumber;
      options.collection.itemsPerPage = itemsPerPageElem.value;
      options.collection.paginationControlsSelector = `${paginationControlsSelector}`;
      options.collection.paginatorName = paginatorName;
      options.spinner = {
        name: `${paginatorName}-spinner`,
        targetSelector: `${paginationControlsSelector}`,
        insertPosition: 'afterbegin',
        withWrapper: false
      };

      const data = { options };
      await handleCollectionUpdate(refreshUrl, data);
    } catch (error) {
      console.log(`Error: ${error.message} on ${error.lineNumber}`);
      const msg = 'There was an error and the collection could not be updated.'
      const notice = new AsyncNotice(msg, 'error');
      await notice.create({}, true);
      notice.remove();
    }
  }
}

/**
 * Process the change of collection items.
 *
 * @param url
 * @param data
 * @returns {Promise<{success: boolean}>}
 */
async function handleCollectionUpdate (url, data) {
  const {options} = data;
  const { collection:  { paginationControlsSelector, paginatorName }, spinner: spinnerOptions } = options;
  const { name, targetSelector, insertPosition = 'afterend', withWrapper = false  } = spinnerOptions;
  const spinner = new Spinner(name, targetSelector, insertPosition, withWrapper);

  try {
    const collectionElement = document.querySelector('[data-paginated-collection]');
    const paginationControls = document.querySelector(`${paginationControlsSelector}`)
    const itemsElem = document.querySelector(`#${paginatorName}-items-per-page`);
    let updateContent= false;

    // add spinner
    spinner.add();

    // fetch & handle updated collection
    const result = await new FetchHelpers().fetchData('POST', url, data);
    if (result.success) {
      // remove any previous notices, if any
      const notice = document.querySelector('#notices-main');
      if(notice) {
        notice.remove();
      }

      // check that we have content to update collection
      let content_present
      = (options.collection.style === 'table' && (result.rows ?? '').length > 0)
      || ((result.collection ?? '').length > 0);
      if(content_present) {
        updateContent = true;
      } else {
        const msg = 'There was an error updating the collection';
        await sendErrorNotices(msg);
      }

      if(updateContent) {
        // set the page param to '1' on url
        const params = new URLSearchParams(location.search);
        // params.delete('page'); // no need to pass page number
        // params.append('page', options.collection.currentPage);
        // window.history.replaceState({}, '', `${location.pathname}?${params}`);
        window.history.replaceState({}, '', `${location.pathname}`);

        // update collection element with new rows
        if (options.collection.style === 'table') {
          // replace table rows
          if (result.rows) {
            collectionElement.tBodies[0].innerHTML = result.rows;
          } else {
            // replace collection html
            collectionElement.insertAdjacentHTML('beforebegin', result.collection);
            collectionElement.remove();
          }
        } else {
          // replace collection html
          collectionElement.insertAdjacentHTML('beforebegin', result.collection);
          collectionElement.remove();
        }

        // update itemsPerPage
        if (itemsElem) {
          itemsElem.value = result.itemsPerPage;
        } else {
          const msg = 'There was an error updating the collection.';
          await sendErrorNotices(msg);
        }

        // update totals
        let totalsSpan = document.querySelector('.annotation-items');
        if(totalsSpan) {
          totalsSpan.innerHTML = result['total'];
        }

        // update navigation controls
        const newNav = document.createRange().createContextualFragment(result.nav);
        if(newNav) {
          paginationControls.replaceWith(newNav);
          await addPaginationNavigationHandlers(paginatorName);
        } else {
          const msg = 'There was an error updating pagination controls.';
          await sendErrorNotices(msg);
        }

        // update options data attribute
        collectionElement.dataset.options = result.options;

        await addAnnotationsActionHandlers();

        if(paginatorName === 'last-locations') {
          addPinEventHandlers();
        }

        // update collection wrapper
        collectionElement.dataset.options = JSON.stringify(options);

        // re-init foundation
        jQuery(document).foundation();
      }

      spinner.remove();
    } else {
      spinner.remove();
      const msg = 'There was an error updating the collection.';
      await sendErrorNotices(msg);
    }
  } catch(error) {
    // await spinner.remove();

    await sendErrorNotices(error.message);

    return { success: false };
  }

  spinner.remove();
  return { success: true };
}

/**
 * Simple error handling with message.
 *
 * @param msg
 */
async function sendErrorNotices(msg) {
  await displayNotice(msg, 'error');
  console.log(msg);
}

/**
 * Add collection-related options to the options object.
 *
 * @param options
 * @param paginatorName
 * @param hasPins
 * @param isEditable
 * @returns {*}
 */
function setCollectionOptions(options, paginatorName, hasPins = false, isEditable = false) {
  const newOptions = structuredClone(options);

  newOptions.collection.paginatorName = paginatorName;
  newOptions.collection.hasPins = hasPins;
  newOptions.collection.isEditable = isEditable;

  return newOptions;
}

/**
 * Adds event handlers to pagination links.
 *
 * @function addPaginationNavigationHandlers
 */
function addPaginationNavigationHandlers(paginatorName) {
  const paginationElem = document.querySelector(`nav#${paginatorName}-nav`);
  const paginationLinks = paginationElem.querySelectorAll(`ul li`);
  if(paginationLinks) {
    paginationLinks.forEach((link) => {
      link.addEventListener('click', async (event) => {
        await paginationHandler(event);
      });
    });
  }
}

export {addPaginationNavigationHandlers, itemsPerPageHandler, paginationHandler, setCollectionOptions}
